// Drop-in <img> replacement that serves responsive WebP via <picture> when the
// image has generated variants (see scripts/generate-images.mjs + imageManifest),
// and falls back to a plain <img> for anything else (SVG, external URLs, unknown
// paths). The original jpg/png is always the <img> fallback, so og:image/schema
// — which keep pointing at originals — are unaffected.

import { imageManifest } from "@/lib/imageManifest";

/** Common `sizes` presets per layout context. */
export const SIZES = {
  full: "100vw",
  split: "(min-width: 768px) 50vw, 100vw",
  card3: "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw",
  card4: "(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw",
  portrait: "(min-width: 768px) 33vw, 50vw",
  logo: "250px",
  thumb: "(min-width: 640px) 33vw, 50vw",
} as const;

type ImgProps = {
  src: string;
  alt: string;
  sizes?: string;
  className?: string;
  width?: number;
  height?: number;
  /** LCP/above-the-fold image: eager + high fetch priority + sync decode. */
  priority?: boolean;
  loading?: "lazy" | "eager";
} & Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  "src" | "srcSet" | "sizes" | "width" | "height" | "loading"
>;

const webpFor = (src: string, w: number) => src.replace(/\.(jpe?g|png)$/i, `-${w}.webp`);

export function Img({
  src,
  alt,
  sizes = SIZES.full,
  className,
  width,
  height,
  priority,
  loading,
  ...rest
}: ImgProps) {
  const entry = imageManifest[src];
  const eager = priority || loading === "eager";

  const imgProps = {
    alt,
    className,
    loading: loading ?? (eager ? "eager" : "lazy"),
    decoding: eager ? ("sync" as const) : ("async" as const),
    ...(priority ? { fetchPriority: "high" as const } : {}),
    ...rest,
  };

  // SVG / external / unknown path → safe passthrough.
  if (!entry) {
    return <img src={src} width={width} height={height} sizes={sizes} {...imgProps} />;
  }

  const webpSrcSet = entry.widths.map((w) => `${webpFor(src, w)} ${w}w`).join(", ");
  return (
    <picture>
      <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} />
      <img
        src={src}
        width={width ?? entry.intrinsicWidth}
        height={height ?? entry.intrinsicHeight}
        sizes={sizes}
        {...imgProps}
      />
    </picture>
  );
}
